import { useEffect, useState } from "react";
import { listFlussMetadata } from "../node-properties/useFlussNodePanelMetadata";
import { metadataToScopeOptions } from "../node-properties/fluss-node-panel-utils";
import { ScopeOption } from "../fluss-builder-types";
import { FlussMetadataOption } from "../node-properties/fluss-node-panel-data";

// Columns are loaded on demand, one table at a time, from the metadata endpoint the Studio
// already uses (sys_columns by sys_table_id, which already includes inherited columns). One
// promise per table name is kept for the lifetime of the page.
const tablesPromise: { current: Promise<FlussMetadataOption[]> | null } = { current: null };
const columnsPromises = new Map<string, Promise<ScopeOption[]>>();

export const loadTableMetadata = (): Promise<FlussMetadataOption[]> => {
    if (!tablesPromise.current) {
        tablesPromise.current = listFlussMetadata("sys_tables").then((tables) => tables || []).catch(() => {
            tablesPromise.current = null;
            return [];
        });
    }
    return tablesPromise.current;
};

export const useTableParents = (enabled: boolean): Record<string, string | undefined> => {
    const [parents, setParents] = useState<Record<string, string | undefined>>({});
    useEffect(() => {
        if (!enabled) return undefined;
        let active = true;
        loadTableMetadata().then((tables) => {
            if (active) setParents(Object.fromEntries(tables.map((table) => [table.name, table.parent_table_name])));
        });
        return () => { active = false; };
    }, [enabled]);
    return parents;
};

export const resetTableColumnsCache = () => {
    tablesPromise.current = null;
    columnsPromises.clear();
};

export const loadTableColumns = (tableName: string): Promise<ScopeOption[]> => {
    const cached = columnsPromises.get(tableName);
    if (cached) return cached;
    const promise = (async () => {
        const tables = await loadTableMetadata();
        const table = tables.find((candidate) => candidate.name === tableName);
        if (!table?.id) return [];
        return metadataToScopeOptions(await listFlussMetadata("sys_columns", { sys_table_id: table.id }));
    })().catch(() => {
        columnsPromises.delete(tableName);
        tablesPromise.current = null;
        return [] as ScopeOption[];
    });
    columnsPromises.set(tableName, promise);
    return promise;
};

export const useTableColumns = (tableName?: string | null): { columns: ScopeOption[]; loading: boolean } => {
    const [state, setState] = useState<{ table: string; columns: ScopeOption[] } | null>(null);
    useEffect(() => {
        if (!tableName) return undefined;
        let active = true;
        loadTableColumns(tableName).then((columns) => {
            if (active) setState({ table: tableName, columns });
        });
        return () => { active = false; };
    }, [tableName]);
    if (!tableName) return { columns: [], loading: false };
    return state?.table === tableName
        ? { columns: state.columns, loading: false }
        : { columns: [], loading: true };
};

export default useTableColumns;
